import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  CustomerListResponse,
  CustomerListParams,
  CustomerDetails,
  WishlistResponseModel,
  Pagination,
  CustomerOrderListResponse,
} from "../Helpers/Interfaces/CustomerTypes";

export const CustomerService = createApi({
  reducerPath: "CustomersApi",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Customers"],
  endpoints: (builder) => ({
    getCustomers: builder.query<CustomerListResponse, CustomerListParams>({
      query: (params) => ({
        url: `${ApiPath.allCustomers}`,
        method: "POST",
        body: params,
      }),
      providesTags: ["Customers"],
    }),
    getCustomerDetails: builder.query<CustomerDetails, string>({
      query: (id) => ({
        url: `${ApiPath.customerById}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: CustomerDetails }) => response.data,
      providesTags: ["Customers"],
    }),
    updateCustomer: builder.mutation<CustomerDetails, any>({
      query: (payload) => {
        // This is a update request
        return {
          url: `${ApiPath.updateCustomer}/${payload.id}`,
          method: "PUT",
          body: payload.body,
        };
      },
      invalidatesTags: ["Customers"],
    }),

    updateCustomerStatus: builder.mutation<
      any,
      { customerId: string; status: string }
    >({
      query: (payload) => ({
        url: `${ApiPath.updateCustomerStatus}/${payload.customerId}?status=${payload.status}`,
        method: "PUT",
        body: { status: payload.status },
      }),
      invalidatesTags: ["Customers"],
    }),

    getCustomersOrdersByCustomerId: builder.query<
      CustomerOrderListResponse,
      CustomerListParams & { customerId: string }
    >({
      query: (params) => ({
        url: `${ApiPath.adminCustomerOrders}/${params.customerId}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "GET",
      }),
    }),

    getWishlist: builder.query<WishlistResponseModel, Pagination>({
      query: (params) => ({
        url: `${ApiPath.getCustomerWishlist}/${params.customerId}?page=${params.page}&pageSize=${params.pageSize}`,
      }),
    }),

    exportCustomers: builder.mutation<
      Blob,
      { search: string; filters: { key: string; value: string | boolean }[] }
    >({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const { search, filters } = args;

        const result = await fetchWithBQ({
          url: ApiPath.customersExport, // now POST /customers/export
          method: "POST",
          body: {
            search: search.trim(),
            format: "csv",
            filters,
          },
          responseHandler: (response: { blob: () => Promise<Blob> }) =>
            response.blob(),
        });

        if (result.error) return { error: result.error };
        return { data: result.data as Blob };
      },
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerDetailsQuery,
  useUpdateCustomerMutation,
  useLazyGetCustomersOrdersByCustomerIdQuery,
  useLazyGetWishlistQuery,
  useUpdateCustomerStatusMutation,
  useExportCustomersMutation,
} = CustomerService;
